import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Target, User2, Loader2, Search, Clipboard, Phone, PhoneCall, Type, X, Repeat } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const TrainingList = () => {
  const [scenarios, setScenarios] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [scenarioStats, setScenarioStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [q, setQ] = useState("");
  const [modeFor, setModeFor] = useState(null); // scenario object when selecting mode
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, a, st] = await Promise.all([
          api.get("/scenarios"),
          api.get("/me/assignments"),
          api.get("/me/scenario-stats"),
        ]);
        setScenarios(s.data);
        setAssignments(a.data || []);
        setScenarioStats(st.data || {});
      } catch (e) { toast.error("Konnte Szenarien nicht laden"); }
      finally { setLoading(false); }
    })();
  }, []);

  const openAssignmentIds = new Set(assignments.filter(a => a.status === "open").map(a => a.scenario_id));
  const isMandatory = (s) => s.is_mandatory === true || openAssignmentIds.has(s.id);

  const filtered = scenarios.filter(s => {
    if (filter === "pflicht") { if (!isMandatory(s)) return false; }
    else if (filter !== "alle" && s.difficulty !== filter) return false;
    if (q && !(s.title.toLowerCase().includes(q.toLowerCase()) || s.description.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const mandatory = filtered
    .filter(s => s.is_mandatory === true)
    .sort((a, b) => (a.order || 99) - (b.order || 99));
  const freie = filtered.filter(s => !s.is_mandatory);
  const mandatoryCount = scenarios.filter(s => s.is_mandatory === true).length + openAssignmentIds.size;

  const start = async (scenario, mode = "text") => {
    setStarting(scenario.id);
    setModeFor(null);
    try {
      const { data } = await api.post("/training/start", { scenario_id: scenario.id, mode });
      navigate(`/training/session/${data.session.id}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        // Backend signals out-of-minutes → open the paywall globally.
        const wallet = e.response.data?.detail?.wallet || null;
        window.dispatchEvent(new CustomEvent("open-paywall", { detail: { wallet } }));
        toast.info("Voice-Guthaben aufgebraucht — wähle ein Paket, um weiterzumachen.");
      } else {
        toast.error("Konnte Training nicht starten");
      }
    } finally { setStarting(null); }
  };

  const FILTERS = [
    { key: "alle", label: "Alle" },
    { key: "pflicht", label: `Pflicht${mandatoryCount > 0 ? " · " + mandatoryCount : ""}` },
    { key: "leicht", label: "Leicht" },
    { key: "mittel", label: "Mittel" },
    { key: "schwer", label: "Schwer" },
  ];

  const ScenarioCard = ({ s, idx, prominent = false }) => {
    const isAssigned = openAssignmentIds.has(s.id);
    const lessonNum = s.is_mandatory ? s.order : null;
    const stat = scenarioStats[s.id];
    return (
      <div
        className={`nv-card nv-card-hover overflow-hidden flex flex-col animate-slide-up ${prominent ? "border-novatus-green/60 shadow-[0_0_24px_rgba(35,224,88,0.12)]" : isAssigned ? "border-novatus-green/50" : ""}`}
        style={{ animationDelay: `${idx * 0.04}s` }}
        data-testid={`scenario-card-${s.id}`}
      >
        <div className={`h-1 ${s.difficulty === "leicht" ? "bg-novatus-green" : s.difficulty === "mittel" ? "bg-amber-500" : "bg-red-500"}`} />
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            {lessonNum && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-novatus-green text-black flex items-center gap-1">
                <Clipboard className="w-3 h-3" /> Lektion {["I","II","III","IV","V"][lessonNum - 1] || lessonNum}
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full diff-${s.difficulty}`}>
              {s.difficulty}
            </span>
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-2 flex items-center gap-1">
              <User2 className="w-3 h-3" /> {s.customer_type}
            </span>
            {isAssigned && !lessonNum && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40 flex items-center gap-1">
                <Clipboard className="w-3 h-3" /> Zuweisung
              </span>
            )}
          </div>
          <h3 className="font-display font-bold text-xl text-white mt-4 leading-tight">{s.title}</h3>
          <p className="text-sm text-novatus-text-2 mt-2 leading-relaxed flex-1">{s.description}</p>
          <div className="mt-4 pt-4 border-t border-novatus">
            <p className="label-eyebrow flex items-center gap-1.5"><Target className="w-3 h-3 text-novatus-green" /> Lernziel</p>
            <p className="text-sm text-white mt-1 leading-snug">{s.learning_goal}</p>
          </div>
          {stat && stat.count > 0 && (
            <div
              className="mt-3 flex items-center gap-2 text-[11px] text-novatus-text-2"
              data-testid={`scenario-stat-${s.id}`}
            >
              <Repeat className="w-3 h-3 text-novatus-green" />
              <span className="font-semibold text-white">{stat.count}×</span>
              <span>trainiert</span>
              <span className="text-novatus-text-3">·</span>
              <span className="font-semibold text-white">Ø {stat.avg_score}</span>
              <span>Score</span>
            </div>
          )}
          <button
            onClick={() => setModeFor(s)}
            disabled={starting === s.id}
            className="btn-green w-full mt-5 h-11 rounded-lg flex items-center justify-center gap-2"
            data-testid={`start-scenario-btn-${s.id}`}
          >
            {starting === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>
              <Play className="w-4 h-4" fill="currentColor" /> Training starten
            </>)}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="animate-slide-up">
        <span className="label-eyebrow">Training</span>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2">
          Wähle dein <span className="text-novatus-green">Szenario.</span>
        </h1>
        <p className="text-novatus-text-2 mt-3 max-w-2xl">
          Jedes Szenario simuliert einen realistischen deutschen Privatkunden zu Wärmepumpe, PV oder PVT.
        </p>
      </div>

      {/* Filters + search */}
      <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="flex flex-wrap gap-2 overflow-x-auto" data-testid="filter-tabs">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`pill-tab ${filter === f.key ? "active" : ""}`}
              data-testid={`filter-${f.key}`}
            >
              {f.key === "pflicht" && <Clipboard className="w-3.5 h-3.5" />}
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-novatus-text-3" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Szenario suchen..."
            className="nv-input w-full h-10 pl-9 text-sm"
            data-testid="search-scenarios"
          />
        </div>
      </div>

      {loading ? (
        <div className="mt-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-novatus-green" /></div>
      ) : filtered.length === 0 ? (
        <div className="nv-card mt-10 p-10 sm:p-12 text-center animate-slide-up" data-testid="training-empty-state">
          <Search className="w-9 h-9 text-novatus-text-3 mx-auto mb-3" />
          <p className="text-novatus-text-2 text-sm">Keine Szenarien passen zu deinem Filter. Setze ihn zurück oder probiere „Alle".</p>
        </div>
      ) : (
        <>
          {mandatory.length > 0 && (
            <section className="mt-10" data-testid="mandatory-section">
              <div className="flex items-baseline gap-3 mb-4">
                <Clipboard className="w-4 h-4 text-novatus-green" />
                <h2 className="font-display font-bold text-lg sm:text-xl text-white">Pflicht-Lektionen</h2>
                <span className="text-xs text-novatus-text-3">Wiederholbar, in jedem Modus übbar.</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {mandatory.map((s, idx) => <ScenarioCard key={s.id} s={s} idx={idx} prominent />)}
              </div>
            </section>
          )}

          {freie.length > 0 && (
            <section className="mt-12" data-testid="free-section">
              <div className="flex items-baseline gap-3 mb-4">
                <Target className="w-4 h-4 text-novatus-text-3" />
                <h2 className="font-display font-bold text-lg sm:text-xl text-white">Freie Übungen</h2>
                <span className="text-xs text-novatus-text-3">Nach Schwierigkeit · zur eigenständigen Weiterentwicklung.</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {freie.map((s, idx) => <ScenarioCard key={s.id} s={s} idx={idx} />)}
              </div>
            </section>
          )}
        </>
      )}

      {/* Mode Selector Modal */}
      {modeFor && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={() => setModeFor(null)}>
          <div className="nv-card max-w-2xl w-full p-6 sm:p-8 my-auto max-h-[calc(100vh-2rem)] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="mode-modal">
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="label-eyebrow">Trainings-Modus wählen</span>
                <h3 className="font-display font-black text-2xl text-white mt-1">{modeFor.title}</h3>
              </div>
              <button onClick={() => setModeFor(null)} className="p-1.5 hover:bg-white/5 rounded-md" data-testid="mode-close-btn"><X className="w-4 h-4 text-novatus-text-2" /></button>
            </div>
            <p className="text-sm text-novatus-text-2 mt-2">Drei Wege, dieses Szenario zu trainieren. Wähle, wie du es heute üben möchtest.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              {/* Text */}
              <button
                onClick={() => start(modeFor, "text")}
                className="group nv-card nv-card-hover text-left p-5 transition-all"
                data-testid="start-mode-text"
              >
                <div className="w-10 h-10 rounded-lg bg-novatus-card-2 border border-novatus flex items-center justify-center mb-3 group-hover:border-novatus-green/50">
                  <Type className="w-5 h-5 text-novatus-text-2 group-hover:text-novatus-green" />
                </div>
                <h4 className="font-display font-bold text-white">Text-Chat</h4>
                <p className="text-xs text-novatus-text-2 mt-1.5 leading-relaxed">KI ist Kunde. Du schreibst als Verkäufer. Schnell & in jeder Umgebung nutzbar.</p>
                <p className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-3 font-semibold">Volle XP</p>
              </button>

              {/* Passive call */}
              <button
                onClick={() => start(modeFor, "passive_call")}
                className="group nv-card nv-card-hover text-left p-5 transition-all"
                data-testid="start-mode-passive"
                style={{ borderColor: "rgba(31, 203, 79, 0.35)" }}
              >
                <div className="w-10 h-10 rounded-lg bg-novatus-green/15 border border-novatus-green/40 flex items-center justify-center mb-3">
                  <PhoneCall className="w-5 h-5 text-novatus-green" />
                </div>
                <h4 className="font-display font-bold text-white">Anruf führen</h4>
                <p className="text-xs text-novatus-text-2 mt-1.5 leading-relaxed">Du rufst an. KI nimmt ab als Kunde. Echtes Call-Training mit Voice + Voice-Analytics.</p>
                <p className="text-[10px] uppercase tracking-wider text-novatus-green mt-3 font-semibold">Empfohlen · Volle XP</p>
              </button>

              {/* Active call */}
              <button
                onClick={() => start(modeFor, "active_call")}
                className="group nv-card nv-card-hover text-left p-5 transition-all"
                data-testid="start-mode-active"
              >
                <div className="w-10 h-10 rounded-lg bg-novatus-card-2 border border-novatus flex items-center justify-center mb-3 group-hover:border-novatus-green/50">
                  <Phone className="w-5 h-5 text-novatus-text-2 group-hover:text-novatus-green" />
                </div>
                <h4 className="font-display font-bold text-white">Anruf annehmen</h4>
                <p className="text-xs text-novatus-text-2 mt-1.5 leading-relaxed">KI ruft an als Novatus-Verkäufer. Du spielst Kunde. Demo-Modus zum Lernen.</p>
                <p className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-3 font-semibold">Demo · Halbe XP</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingList;
